// src/components/Replan/TextLabelShape.js
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Group as KonvaGroup } from 'react-konva';
import DrawingText from './DrawingText';
import ResizeAnchor from './ResizeAnchor';
import { TOOLS } from '../constants/drawingTools';

/**
 * TextLabelShape — A reusable text label component (title + body) with a
 * draggable text-anchor control point.
 *
 * Can be used standalone (as the TEXT tool) or composed inside other shapes
 * such as RectangleShape.
 *
 * Props
 * ─────
 * @param {object}   shape              – The shape object (must contain id, text, stroke, strokeWidth).
 * @param {object}   commonProps        – Shared Konva props (stroke, strokeWidth, draggable …).
 * @param {boolean}  isSelected         – Whether the parent shape / this shape is selected.
 * @param {number}   stageScale         – Current stage zoom level.
 * @param {string}   shapeStroke        – Original (non-selection) stroke colour.
 * @param {Function} onStartTextEdit    – Callback to begin inline text editing.
 * @param {string}   textEditingShapeId – ID of the shape currently being edited (hides text while editing).
 * @param {Function} onUpdateShape      – Callback to persist geometry / offset changes.
 * @param {boolean}  standalone         – When true the component renders its own draggable Group (TEXT tool).
 */
const TextLabelShape = ({
  shape,
  commonProps,
  isSelected,
  stageScale,
  shapeStroke,
  onStartTextEdit,
  textEditingShapeId,
  onUpdateShape,
  standalone = false,
}) => {
  const textRef = useRef(null);
  const bodyTextRef = useRef(null);
  const [isDraggingText, setIsDraggingText] = useState(false);

  // ── Auto-trigger text edit for new shapes ──────────────────────────────
  useEffect(() => {
    if (shape.pendingTextEdit && onStartTextEdit && textRef.current) {
      // Small timeout to ensure Konva has finished mounting/rendering the node
      const timer = setTimeout(() => {
        onStartTextEdit(shape.id, textRef.current);
        if (onUpdateShape) {
          onUpdateShape(shape.id, { pendingTextEdit: false });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [shape.pendingTextEdit, shape.id, onStartTextEdit, onUpdateShape]);

  // ── Parse title / body from shape.text using "---" separator ───────────
  const { title, body } = useMemo(() => {
    const text = shape.text || shape.id + '\n---\nBody text here...';
    const parts = text.split('\n---\n');
    return {
      title: parts[0] || shape.id,
      body: parts.slice(1).join('\n---\n') || '',
    };
  }, [shape.text, shape.id]);

  // ── Font sizes ─────────────────────────────────────────────────────────
  const titleFontSize = Math.max(12, 16 / stageScale, (commonProps.strokeWidth || 2) * 3);
  const bodyFontSize = Math.max(12, 12 / stageScale, (commonProps.strokeWidth || 2) * 2);
  const isEditing = textEditingShapeId === shape.id;

  // Text offset relative to the parent (or to its own origin if standalone)
  const textOffsetX = shape.textOffsetX !== undefined ? shape.textOffsetX : 0;
  const textOffsetY = shape.textOffsetY !== undefined ? shape.textOffsetY : 0;

  // ── Control-point styling ──────────────────────────────────────────────
  const controlPointRadius = Math.max(4, 6 / stageScale);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleTextEdit = () => {
    if (onStartTextEdit && textRef.current) {
      onStartTextEdit(shape.id, textRef.current);
    }
  };

  const handleTextAnchorDrag = (e) => {
    const node = e.target;
    const newX = node.x();
    const newY = node.y();

    if (textRef.current) {
      textRef.current.position({ x: newX, y: newY });
    }
    if (bodyTextRef.current) {
      bodyTextRef.current.position({ x: newX, y: newY + titleFontSize + 5 });
    }

    if (shape.type === TOOLS.ARROW && onUpdateShape) {
      onUpdateShape(shape.id, {
        textOffsetX: newX,
        textOffsetY: newY,
        points: [newX, newY, shape.points[2], shape.points[3]]
      });
    }

    node.getLayer()?.batchDraw();
  };

  const handleTextAnchorDragStart = () => setIsDraggingText(true);

  const handleTextAnchorDragEnd = (e) => {
    if (!onUpdateShape) return;
    const node = e.target;
    const newX = node.x();
    const newY = node.y();

    const updates = {
      textOffsetX: newX,
      textOffsetY: newY,
    };
    if (shape.type === TOOLS.ARROW && shape.points && shape.points.length === 4) {
      updates.points = [newX, newY, shape.points[2], shape.points[3]];
    }

    onUpdateShape(shape.id, updates);
    setIsDraggingText(false);
  };

  // ── Rendered elements ──────────────────────────────────────────────────
  const textElements = (
    <>
      {/* Title Text (bold, larger) */}
      {(titleFontSize < 160 || isSelected) && (
        <DrawingText
          textRef={textRef}
          x={textOffsetX}
          y={textOffsetY}
          text={title}
          fontSize={titleFontSize}
          fontStyle="bold"
          fill={commonProps.stroke}
          align="left"
          listening={!isDraggingText}
          onDblClick={handleTextEdit}
          onDblTap={handleTextEdit}
          visible={!isEditing}
          stageScale={stageScale}
          useBadge={false}
        />
      )}

      {/* Body Text (normal, multi-line) */}
      {body && (bodyFontSize < 35 || isSelected) && (
        <DrawingText
          textRef={bodyTextRef}
          x={textOffsetX}
          y={textOffsetY + titleFontSize + 5}
          text={body}
          fontSize={bodyFontSize}
          fontStyle="normal"
          fill={commonProps.stroke}
          align="left"
          listening={!isDraggingText}
          onDblClick={handleTextEdit}
          onDblTap={handleTextEdit}
          width={bodyFontSize * 20}
          wrap="word"
          visible={!isEditing}
          stageScale={stageScale}
          useBadge={false}
        />
      )}

      {/* Text Anchor – only shown when selected */}
      {isSelected && (
        <ResizeAnchor
          x={textOffsetX}
          y={textOffsetY}
          size={controlPointRadius * 0.8}
          stageScale={stageScale}
          color={commonProps.stroke}
          cursor={commonProps.draggable ? 'move' : 'pointer'}
          hoverCursor="move"
          onDragMove={handleTextAnchorDrag}
          onDragStart={handleTextAnchorDragStart}
          onDragEnd={handleTextAnchorDragEnd}
        />
      )}
    </>
  );

  // ── Standalone mode: wrap in its own draggable Group ───────────────────
  if (standalone) {
    return (
      <KonvaGroup
        {...commonProps}
        draggable={commonProps.draggable && !isDraggingText}
        onDblClick={handleTextEdit}
        onDblTap={handleTextEdit}
      >
        {textElements}
      </KonvaGroup>
    );
  }

  // ── Composed mode: return bare elements (caller provides the Group) ───
  return textElements;
};

export default TextLabelShape;
